// Shapes mirror what apps/distri_ai returns today, verified against live
// responses and service source. Keys that only some plans receive are optional.

// ─── Shared ────────────────────────────────────────────────────────────────

/** Body of a 402 from any plan-gated DistriAI endpoint. */
export interface AIUpgradeRequired {
  detail: string
  upgrade_required: true
}

// ─── Anomalies ─────────────────────────────────────────────────────────────

export type AnomalyMethod = 'isolation_forest' | 'zscore' | 'insufficient_data' | 'none'

/** What the series is made of: checkout-day completed sales, or a lodge's nightly room series from the night audit. */
export type AnomalyBasis = 'sales' | 'room_nights'

export interface AnomalyItem {
  date: string
  revenue: number
  /**
   * A real z-score on the 'zscore' method. On 'isolation_forest' it is the
   * revenue residual against that weekday's median, in standard deviations.
   */
  z_score: number
  type: 'spike' | 'drop'
  /** Isolation Forest only. Higher means more isolated. */
  anomaly_score?: number
  /** Room-night basis only: rooms sold that night. */
  rooms_sold?: number
}

export interface AnomalyResult {
  period_days: number
  mean_daily_revenue: number
  stdev_daily_revenue: number
  /** 'insufficient_data' means too few sales days to judge. It is not the same as "no anomalies". */
  method: AnomalyMethod
  /** Absent on responses from before the field existed: treat as 'sales'. */
  basis?: AnomalyBasis
  anomalies: AnomalyItem[]
}

// ─── Forecast ──────────────────────────────────────────────────────────────

export type ForecastMethod = 'ema' | 'ml'

export interface ForecastDay {
  date: string
  forecast: number
  /** Bands come from the sellable's historical volatility in both methods, not from model uncertainty. */
  ci_80_low: number
  ci_80_high: number
  ci_95_low: number
  ci_95_high: number
}

export interface ForecastItem {
  sellable_id: string
  sellable_name: string
  historical_avg_daily: number
  ema_last: number
  forecast_next_period: number
  forecast_by_day: ForecastDay[]
  current_stock: number
  /** null when demand is negligible. */
  stock_covers_days: number | null
  seasonality_applied: boolean
  forecast_method: ForecastMethod
}

// ─── Upsell and stockouts ──────────────────────────────────────────────────

export interface UpsellPair {
  product_a: string
  product_b: string
  support: number
  confidence: number
  lift: number
}

export interface StockoutCandidate {
  listing_id: string
  sellable_id: string
  name: string
  units_sold_last_30d: number
  current_stock: number
}

// ─── Margins ───────────────────────────────────────────────────────────────

export interface MarginMonth {
  month: string
  revenue: number
  units: number
  gp_pct: number
}

export interface MarginSummaryItem {
  sellable_id: string
  name: string
  cost_price: number
  months: MarginMonth[]
  trend: 'compressing' | 'stable' | 'improving'
  latest_gp_pct: number
  mom_change_pp: number
}

export interface MarginIntelligenceResult {
  summary: MarginSummaryItem[]
  most_compressed: MarginSummaryItem[]
  most_profitable: MarginSummaryItem[]
  alert_count: number
}

// ─── Trending ──────────────────────────────────────────────────────────────

export interface TrendingListing {
  listing_id: string
  name: string
  current_hour_views: number
  baseline_hourly_views: number
  multiplier: number
}

// ─── Cash flow ─────────────────────────────────────────────────────────────

export interface CashFlowHorizon {
  days: number
  net_cash_flow: number
  notes: string[]
}

export interface CashFlowResult {
  receivables_total: number
  payables_total: number
  recurring_expenses_30d: number
  estimated_payroll_30d: number
  forecast: CashFlowHorizon[]
  key_risks: string[]
}

// ─── Smart reorder ─────────────────────────────────────────────────────────

export interface ReorderItem {
  sellable_id: string
  name: string
  current_stock: number
  daily_demand: number
  stock_covers_days: number
  lead_time_days: number
  suggested_qty: number
  priority: 'critical' | 'urgent' | 'soon'
  last_supplier: { id: string; name: string } | null
}

/** The endpoint returns a bare list, most urgent first. */
export type SmartReorderResult = ReorderItem[]

// ─── Suppliers ─────────────────────────────────────────────────────────────

export interface SupplierPriceAlert {
  supplier_id: string
  supplier_name: string
  sellable_id: string
  sellable_name: string
  avg_unit_cost_3m: number
  latest_unit_cost: number
  increase_pct: number
}

export interface SupplierReliability {
  supplier_id: string
  name: string
  po_count: number
  fill_rate_pct: number
}

export interface SupplierIntelligenceResult {
  price_alerts: SupplierPriceAlert[]
  supplier_reliability: SupplierReliability[]
}

// ─── Payroll ───────────────────────────────────────────────────────────────

export interface PayrollAnomaly {
  run_id: string
  period_label: string
  total_gross_pay: number
  z_score: number
  type: 'spike' | 'drop'
}

export interface PayrollHeadcountAlert {
  run_id: string
  period_label: string
  prev_count: number
  curr_count: number
  delta: number
}

export interface PayrollRunPoint {
  run_id: string
  period_end: string
  period_label: string
  total_gross_pay: number
  /** null with fewer than 3 runs or when every run is identical. */
  z_score: number | null
  anomaly: boolean
}

/** With fewer than 3 approved or paid runs the statistics are 0 and nothing is flagged, but runs is still filled. */
export interface PayrollAnomalyResult {
  period_runs: number
  /** Every approved or paid run, oldest first, for charting. */
  runs: PayrollRunPoint[]
  mean_gross_pay: number
  stdev_gross_pay: number
  anomalies: PayrollAnomaly[]
  headcount_alerts: PayrollHeadcountAlert[]
}

// ─── Churn ─────────────────────────────────────────────────────────────────

export interface ChurnBacktestSummary {
  n: number | null
  n_pos: number | null
  auc: number | null
  model_logloss: number | null
  base_logloss: number | null
  /** e.g. 'insufficient_history' */
  reason: string | null
}

export interface ChurnCustomer {
  customer_id: string
  name: string
  /** Probability of not returning within horizon_days. */
  churn_risk: number
  last_visit: string
  days_since_last_visit: number
  visits: number
  typical_gap_days: number | null
}

interface ChurnInfo {
  horizon_days: number
  trained_at: string
}

/**
 * Only 'ok' carries scores. 'not_proven' is the expected state until the model
 * beats the base rate on held-out data. There is deliberately no fallback method.
 */
export type ChurnResult =
  | { status: 'no_model' }
  | { status: 'unavailable' }
  | ({ status: 'stale_model' } & ChurnInfo)
  | ({ status: 'not_proven'; backtest: ChurnBacktestSummary } & ChurnInfo)
  | ({ status: 'ok'; as_of: string; n_scored: number; customers: ChurnCustomer[] } & ChurnInfo)

// ─── Digest ────────────────────────────────────────────────────────────────

export interface AIDigestMetrics {
  revenue_this_week: number
  revenue_prev_week: number
  revenue_trend_pct: number
  transaction_count: number
  top_product: { name: string; units: number } | null
  stockout_count: number
}

export interface HospitalityPeriodStats {
  room_nights_sold: number
  room_nights_available: number
  occupancy_pct: number | null
  room_revenue: number
  adr: number | null
  revpar: number | null
  comp_nights: number
}

export interface HospitalityNight {
  date: string
  sold: number
  available: number
  occupancy_pct: number | null
  revenue: number
}

export interface HospitalityForwardNight {
  date: string
  booked: number
  available: number
  occupancy_pct: number | null
}

export interface HospitalityOverdue {
  booking_reference: string
  room_type: string
  expected_checkout: string
  days_overdue: number
}

/** Night-based room metrics (accrual basis, from the night audit's room charges), for businesses with rooms. */
export interface HospitalitySummary {
  period: { from: string; to: string; nights: number }
  capacity_basis: 'room_units' | 'room_types'
  rooms: number
  this_period: HospitalityPeriodStats
  previous_period: HospitalityPeriodStats
  occupancy_change_pts: number | null
  revenue_change_pct: number | null
  by_night: HospitalityNight[]
  lowest_night: HospitalityNight | null
  forward: HospitalityForwardNight[]
  overdue_checkouts: { count: number; items: HospitalityOverdue[] }
}

/** Returned before the first digest has been computed. */
export interface AIDigestPending {
  detail: string
  computed_at: null
}

/**
 * Free:       computed_at, metrics, stockout_count, teaser
 * Starter:    + anomalies, forecast_summary, top_upsell_pair
 * Growth:     + margin_health, cash_runway_days, trending_listings, narrative, recommendations, hospitality
 * Enterprise: + supplier_alerts, payroll_health
 * The payload carries no plan tag and no period.
 */
export interface AIDigest {
  computed_at: string
  metrics: AIDigestMetrics
  stockout_count?: number
  teaser?: string
  anomalies?: AnomalyItem[]
  forecast_summary?: ForecastItem[]
  top_upsell_pair?: UpsellPair | null
  margin_health?: { alert_count: number; most_compressed: MarginSummaryItem[] }
  /** Despite the name this is the 30-day net cash flow in KES (can be negative), not a number of days. */
  cash_runway_days?: number | null
  trending_listings?: TrendingListing[]
  narrative?: string | null
  recommendations?: string[]
  /** Night-based room metrics for businesses with room resources (Growth+). */
  hospitality?: HospitalitySummary | null
  supplier_alerts?: SupplierPriceAlert[]
  payroll_health?: PayrollAnomalyResult
}

export type AIDigestResponse = AIDigest | AIDigestPending

// ─── Page tabs ─────────────────────────────────────────────────────────────

/** Single source of truth for the page's tabs: the route's search validation and the page both use it. */
export const AI_TAB_KEYS = [
  'digest',
  'forecast',
  'anomalies',
  'margin',
  'cash-flow',
  'reorder',
  'churn',
  'suppliers',
  'payroll',
  'chat',
] as const

export type AITab = (typeof AI_TAB_KEYS)[number]
